/**
 * Axios instance с interceptors: автоматически добавляет JWT токен в заголовки
 * Обрабатывает 401 ошибки (перенаправление на /login)
 * Дата: 24.01.2026 - Добавлено логирование сетевых запросов
 */
import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { logger } from '@/utils/logger';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// ════════════════════════════════════════════════════════════════════════════
// Request Interceptor
// ════════════════════════════════════════════════════════════════════════════

// Храним время начала запроса
const requestStartTimes = new Map<string, number>();

// Генератор уникального ID для запроса
const generateRequestId = (): string => 
  Math.random().toString(36).substring(2, 10);

// Request interceptor - добавляет токен и tenant_id для SuperAdmin
// Дата: 15.01.2026 - Автоматическое добавление tenant_id для SuperAdmin
// Дата: 24.01.2026 - Добавлен tracking времени запроса
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { token, getEffectiveTenantId } = useAuthStore.getState();
  
  // Добавить JWT токен
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Автоматически добавить tenant_id в query params если:
  // 1. Это SuperAdmin (getEffectiveTenantId вернёт selectedTenantId)
  // 2. tenant_id ещё не указан в params
  const tenantId = getEffectiveTenantId();
  if (tenantId && !config.params?.tenant_id) {
    config.params = { ...config.params, tenant_id: tenantId };
  }
  
  // Tracking времени для логирования
  const requestId = generateRequestId();
  config.headers['X-Request-Id'] = requestId;
  requestStartTimes.set(requestId, Date.now());
  
  return config;
});

// ════════════════════════════════════════════════════════════════════════════
// Response Interceptor
// ════════════════════════════════════════════════════════════════════════════

// Response interceptor - обрабатывает 401 и логирует запросы
// Дата: 15.01.2026 - Исправлено: не редиректить если уже на /login (бесконечный цикл)
// Дата: 24.01.2026 - Добавлено логирование сетевых запросов
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    logNetworkRequest(response.config, response.status);
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status || 0;
    const config = error.config;
    
    // Логируем ошибку
    if (config) {
      logNetworkRequest(config, status, error.message);
    }
    
    if (status === 401) {
      useAuthStore.getState().logout();
      // Не редиректить если уже на странице логина
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ════════════════════════════════════════════════════════════════════════════
// Network Logging Helper
// ════════════════════════════════════════════════════════════════════════════

function logNetworkRequest(
  config: InternalAxiosRequestConfig,
  status: number,
  errorMessage?: string
): void {
  // Не логируем запросы к endpoint логирования (избегаем рекурсии)
  if (config.url?.includes('frontend-logs')) {
    return;
  }
  
  // Получаем время запроса
  const requestId = config.headers?.['X-Request-Id'] as string;
  const startTime = requestStartTimes.get(requestId);
  const durationMs = startTime ? Date.now() - startTime : 0;
  
  // Очищаем map
  if (requestId) {
    requestStartTimes.delete(requestId);
  }
  
  // Формируем URL без base
  const url = config.url || '';
  const method = (config.method || 'GET').toUpperCase();
  
  // Логируем через наш logger
  logger.logNetworkRequest(method, url, status, durationMs, errorMessage);
}
